import React, { useState, useEffect } from 'react';
import { Alert, Space, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { ServiceName, useServiceStatus } from '@/contexts/service-status.context';

const { Text } = Typography;

const SERVICE_NAMES: Record<ServiceName, string> = {
  auth: 'Autenticacao',
  user: 'Usuarios',
  registration: 'Cadastros',
  inspection: 'Inspecoes',
  checklist: 'Checklists',
  notification: 'Notificacoes',
  scraper: 'Scrapers',
};

interface FeatureAlertProps {
  services?: ServiceName[];
  style?: React.CSSProperties;
}

export function FeatureAlert({ services, style }: FeatureAlertProps): JSX.Element | null {
  const { offlineServices, status } = useServiceStatus();
  const [dismissed, setDismissed] = useState(false);
  const [lastOfflineServices, setLastOfflineServices] = useState<ServiceName[]>([]);

  const relevantOfflineServices = services
    ? offlineServices.filter(s => services.includes(s))
    : offlineServices;

  useEffect(() => {
    const hasChanged = relevantOfflineServices.length !== lastOfflineServices.length ||
      relevantOfflineServices.some(s => !lastOfflineServices.includes(s));

    if (hasChanged) {
      setDismissed(false);
      setLastOfflineServices(relevantOfflineServices);
    }
  }, [relevantOfflineServices, lastOfflineServices]);

  if (dismissed || relevantOfflineServices.length === 0) {
    return null;
  }

  // Don't show if auth is offline (SystemOffline handles that)
  if (relevantOfflineServices.includes('auth')) {
    return null;
  }

  const offlineServiceNames = relevantOfflineServices.map(s => SERVICE_NAMES[s]).join(', ');

  return (
    <Alert
      type="warning"
      icon={<WarningOutlined />}
      message={
        <Space>
          <Text strong>Algumas funcionalidades podem estar limitadas</Text>
        </Space>
      }
      description={
        <Text type="secondary">
          {relevantOfflineServices.length === 1
            ? `O servico de ${offlineServiceNames} esta temporariamente indisponivel.`
            : `Os servicos de ${offlineServiceNames} estao temporariamente indisponiveis.`}
        </Text>
      }
      closable
      onClose={() => setDismissed(true)}
      showIcon
      style={{
        marginBottom: '16px',
        ...style,
      }}
    />
  );
}

export default FeatureAlert;
